// @flow

import Immutable, {List, Map} from 'immutable';
import {useDispatch} from 'react-redux';
import merge from 'lodash/merge';
import isEqual from 'lodash/isEqual';

import {getQueryParam} from 'app/utils/urls';
import type {ResponseType} from 'app/utils/redux_v2';

export type fetchDataFormatType<T> = {
  byId: {[string]: T},
  allIds: Array<string>,
  count: number,
  next: null | string,
  previous: null | string,
  numPages: number,
};

type BasicRequestResponseMapType = Map<string, any>;

export function mergeOverwrittingArrays(
  a: Map<string, any>,
  b: Map<string, any> | {}
): {} {
  // https://github.com/facebook/immutable-js/issues/762#issuecomment-213566603
  if (a && a.mergeWith && !List.isList(a) && !List.isList(b)) {
    return a.mergeWith(mergeOverwrittingArrays, b);
  }
  return b;
}

export const normalize = (list: Array<any>) =>
  list.reduce(
    (obj, value) => {
      const id = value.id != null ? value.id.toString() : value.url;
      obj.byId[id] = value;
      obj.allIds.push(id);
      return obj;
    },
    {byId: {}, allIds: []}
  );

const extractStats = <T>(fetchResults: ResponseType<T>) => {
  return {
    count: fetchResults.count,
    numPages: fetchResults.num_pages,
    next: getQueryParam(fetchResults.next, 'page'),
    previous: getQueryParam(fetchResults.previous, 'page', '1'),
  };
};

export const normalizeFetchData = <T>(fetchResults: ResponseType<T>) => {
  let normalizedData = normalize(fetchResults.results);
  merge(normalizedData, extractStats(fetchResults));
  return normalizedData;
};

export const normalizeArrayFetchData = (fetchResults: ResponseType<any>) => {
  const fetchIds = (r: any): number => r.id;
  let normalizedData = {
    allIds: fetchResults.results.map<any>(fetchIds),
  };
  merge(normalizedData, extractStats(fetchResults));
  return normalizedData;
};

export const mergeNormalizedFetchData = <T>(
  fetchResults: ResponseType<T>,
  mergeWith?: BasicRequestResponseMapType,
  append?: boolean = false
): {} => {
  const normalizedResult = normalizeFetchData(fetchResults);

  if (append && mergeWith != null) {
    // allow concat result from multiple pages to render a continuous
    // list (e.g. infinite scroll)
    const currentAllIds = mergeWith.get('allIds', List()).toJS();
    normalizedResult.allIds.splice(0, 0, ...currentAllIds);
  }

  const immutableNormalizedResult: any = Immutable.fromJS(normalizedResult);

  return mergeWith != null
    ? // merge byId, allIds and other root keys only
      mergeWith.mergeWith(
        (oldVal, newVal, key) =>
          key === 'byId' ? oldVal.merge(newVal) : newVal,
        immutableNormalizedResult
      )
    : immutableNormalizedResult;
};

export const mergeNormalizedArrayFetchData = (
  fetchResults: ResponseType<any>,
  mergeWith: BasicRequestResponseMapType
) =>
  mergeWith
    ? mergeOverwrittingArrays(mergeWith, normalizeArrayFetchData(fetchResults))
    : Immutable.fromJS(normalizeArrayFetchData(fetchResults));

export const defaultFetchDataFormat = () => ({
  byId: {},
  allIds: [],
  count: 0,
  next: null,
  previous: '1',
  numPages: 0,
});

export const objectsToFlatArray = (immutableDataSet: Map<string, any>) => {
  const dataSetObject: any = immutableDataSet.toJS();
  return dataSetObject.allIds.map((id) => dataSetObject.byId[id]);
};

export const limitRequests = <
  A: {
    (...any): any,
    promise?: Promise<any>,
    clean: () => void,
    args?: any,
  }
>(
  func: A,
  cacheTime?: number = 0
): A => {
  // this function act like a python decorator and should limit the
  // func argument function execution to be executed only when there is
  // no previous executed with the same parameters pending promises
  // (no concurrent similar requests)

  // the func argument should be an async action (should return another func)
  // and the inner func should return a promise

  // save to function 'clean' property a function to clear the cache
  func.clean = () => {
    func.promise = undefined;
    func.args = undefined;
  };

  // $FlowFixMe
  return (...args: any) => {
    // if there is a pending promise and the function was called with
    // same arguments than the last call, just return the pending promise
    // and avoid a new request
    if (func.promise && isEqual(func.args, args)) {
      return () => func.promise;
    }

    // so there is no pending promise or the arguments are different,
    // so the request will be made

    // cache the arguments in the function args property
    func.args = args;

    // execute the outer function to get inner function
    // (which will return the promise)
    const innerFunc = func(...args);

    // get the inner function parameters (dispatch, getState)
    return (...innerArgs: any) => {
      // execute the inner function and save it's return in the
      // function 'promise' attribute
      return (func.promise = innerFunc(...innerArgs).then(
        // clear caches on promise reject or fulfillment
        // and don't change the returned data/error
        (data) => {
          cacheTime > 0 ? setTimeout(func.clean, cacheTime) : func.clean();
          return data;
        },
        (error) => {
          func.clean();
          throw error;
        }
      ));
    };
  };
};

export function useAsyncActions<T: {[string]: (...args: any) => any}>(
  actions: T
): {[$Keys<T>]: (...args: any) => Promise<any>} {
  const dispatch = useDispatch();

  return Object.keys(actions).reduce((asyncActions, key) => {
    asyncActions[key] = (...args) => dispatch(actions[key](...args));
    return asyncActions;
  }, {});
}
